import {bindDialog, bindTrigger, usePopupState} from "material-ui-popup-state/hooks";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import {capitalize} from "@mui/material/utils";
import {Form, Formik} from "formik";
import {useTranslation} from "react-i18next";
import {AiToAiFormValues, useAiToAiTestCallForm} from "../../hooks/aufgaben/useAiToAiTestCallForm";

interface Props {
    aufgabeId: string;
}

export default function ButtonShowAiToAiDialog({ aufgabeId }: Props) {
    const popupState = usePopupState({ variant: "dialog", popupId: "show-ai-to-ai-call-dialog" });
    const { t: translation } = useTranslation("aufgaben");

    const formikProps = useAiToAiTestCallForm(aufgabeId, {
        onSuccess: () => popupState.close(),
    });

    return (
        <>
            <Button
                id="button-show-ai-to-ai-call"
                color="primary"
                variant="outlined"
                aria-label="button-show-ai-to-ai-call"
                {...bindTrigger(popupState)}
            >
                {capitalize(translation("ai-to-ai-test-call"))}
            </Button>

            <Dialog
                id="dialog-show-ai-to-ai-call"
                {...bindDialog(popupState)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle id="dialog-title-show-ai-to-ai-call">
                    {capitalize(translation("task-test"))}
                </DialogTitle>

                <Formik<AiToAiFormValues> {...formikProps}>
                    {({ values, handleChange, handleBlur, touched, errors, isSubmitting }) => (
                        <Form>
                            <DialogContent id="dialog-content-show-ai-to-ai-call">
                                <Box mt={2}>
                                    <TextField
                                        id="telefonnummer"
                                        name="telefonnummer"
                                        label={capitalize(translation("task-test-telefonnummer"))}
                                        value={values.telefonnummer}
                                        fullWidth
                                        onChange={handleChange}
                                        error={touched.telefonnummer && Boolean(errors.telefonnummer)}
                                        helperText={touched.telefonnummer && errors.telefonnummer}
                                        slotProps={{
                                            inputLabel: {shrink: true}
                                        }}
                                    />
                                </Box>

                                <Box mt={2}>
                                    <TextField
                                        id="externalAiConfigEndpointUrl"
                                        name="externalAiConfigEndpointUrl"
                                        label={capitalize(translation("ai-config-endpoint-url"))}
                                        value={values.externalAiConfigEndpointUrl}
                                        fullWidth
                                        onChange={handleChange}
                                        error={
                                            touched.externalAiConfigEndpointUrl &&
                                            Boolean(errors.externalAiConfigEndpointUrl)
                                        }
                                        helperText={
                                            touched.externalAiConfigEndpointUrl &&
                                            errors.externalAiConfigEndpointUrl
                                        }
                                        slotProps={{
                                            inputLabel: {shrink: true}
                                        }}
                                    />
                                </Box>

                                <Box mt={2}>
                                    <TextField
                                        id="externalSpeechModelPrompt"
                                        name="externalSpeechModelPrompt"
                                        label={capitalize(translation("external-speech-model-prompt"))}
                                        value={values.externalSpeechModelPrompt}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        error={
                                            touched.externalSpeechModelPrompt &&
                                            Boolean(errors.externalSpeechModelPrompt)
                                        }
                                        helperText={
                                            touched.externalSpeechModelPrompt &&
                                            errors.externalSpeechModelPrompt
                                        }
                                        slotProps={{
                                            inputLabel: {shrink: true}
                                        }}
                                    />
                                </Box>
                            </DialogContent>

                            <DialogActions>
                                <Button
                                    id="dialog-action-cancel-show-ai-to-ai-call"
                                    color="inherit"
                                    onClick={() => popupState.close()}
                                >
                                    {capitalize(translation("abort"))}
                                </Button>
                                <Button
                                    id="dialog-action-confirm-show-ai-to-ai-call"
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting}
                                    startIcon={
                                        isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined
                                    }
                                >
                                    {capitalize(translation("test"))}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
}
